/**
 * The Sketch's basemap controls (phase 4): the provider switch (Google, Esri, a national open orthophoto where one
 * covers the site, the installer's own picture), uploading a drone or orthophoto picture, and placing it (drag, turn
 * and scale it over the satellite view, or 2–3 control points; a GeoTIFF places itself). Presentational: the canvas
 * draws the picture and takes the gestures (align.ts maths); styles `.sk-basemap…`, `.sk-align…` in sketch.css.
 */
import { Check, ImageUp, Layers, MapPin, Move, Trash2, X } from "lucide-react";
import { useRef, useState, type CSSProperties } from "react";
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys";
import type { Text } from "./text.ts";
import { useSketchText } from "./useText.ts";

type Choice = S["BasemapChoice"];
type Upload = S["BasemapUpload"];

/** Providers' names (brands, never translated); the installer's own picture in words. */
const SHORT: Record<string, string> = {
  google_static: "Google",
  esri_world: "Esri",
  usgs_naip: "USGS NAIP",
  pdok_luchtfoto: "PDOK",
  basemap_at: "basemap.at",
  ign_ortho: "IGN",
};
export const providerShort = (tx: Text, key: string) => (key === "upload" ? tx.t("sketch.basemap.yourPicture") : SHORT[key] ?? key);

const SOURCE: Record<Upload["source"], MessageKey> = { unaligned: "sketch.basemap.source.unaligned", geotiff: "sketch.basemap.source.geotiff", control_points: "sketch.basemap.source.controlPoints", manual: "sketch.basemap.source.manual" };

export function BasemapMenu(props: {
  data: S["DesignBasemap"] | undefined;
  current: string;
  readOnly: boolean;
  busy: boolean;
  error: string | null;
  onChoose: (key: string | null, uploadId?: string) => void;
  onUpload: (file: File) => void;
  onAlign: (upload: Upload) => void;
  onDelete: (id: string) => void;
}) {
  const tx = useSketchText();
  const [open, setOpen] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const choices = props.data?.choices ?? [];
  const uploads = props.data?.uploads ?? [];
  return (
    <div className={`sk-basemap-switch${open ? " is-open" : ""}`}>
      <button type="button" className="sk-basemap-btn sk-glass" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)} title={tx.t("sketch.basemap.title")}>
        <Layers size={16} />
        <span>{providerShort(tx, props.current)}</span>
      </button>
      {open && (
        <div className="sk-basemap-pop sk-glass" role="dialog" aria-label={tx.t("sketch.basemap.title")}>
          <div className="sk-basemap-head">
            <b>{tx.t("sketch.basemap.title")}</b>
            <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.common.close")} onClick={() => setOpen(false)}><X size={14} /></button>
          </div>
          <ul className="sk-basemap-list" role="radiogroup" aria-label={tx.t("sketch.basemap.provider")}>
            {choices.filter((c) => c.kind !== "upload").map((c) => (
              <ChoiceRow key={c.key} c={c} active={props.current === c.key} disabled={props.readOnly || !c.available || props.busy} onPick={() => { setOpen(false); props.onChoose(c.key); }} />
            ))}
          </ul>
          <div className="sk-basemap-own">
            <span className="sk-label">{tx.t("sketch.basemap.own")} <span>{tx.t("sketch.basemap.ownDetail")}</span></span>
            {uploads.map((u) => (
              <div key={u.id} className={`sk-upload${props.current === "upload" && props.data?.upload_id === u.id ? " is-active" : ""}`}>
                <span className="sk-upload-icon"><ImageUp size={15} /></span>
                <span className="sk-upload-text">
                  <b title={u.file_name} dir="auto">{u.file_name}</b>
                  <small>
                    {tx.t(SOURCE[u.source])}
                    {u.metres_per_px ? ` · ${tx.t("sketch.basemap.cmPerPx", { cm: tx.num(u.metres_per_px * 100, 1) })}` : ""}
                    {u.residual_m != null && u.source === "control_points" ? ` · ${tx.t("sketch.basemap.residual", { cm: tx.num(u.residual_m * 100, 0) })}` : ""}
                  </small>
                </span>
                <span className="sk-upload-actions">
                  <button type="button" className="sk-chip" disabled={props.readOnly} onClick={() => { setOpen(false); props.onAlign(u); }}><Move size={13} /> {tx.t("sketch.basemap.place")}</button>
                  <button type="button" className={`sk-chip${props.current === "upload" && props.data?.upload_id === u.id ? " is-active" : ""}`} disabled={props.readOnly || !u.px_to_enu || props.busy} onClick={() => props.onChoose("upload", u.id)}>{tx.t("sketch.structures.showShort")}</button>
                  <button type="button" className="sk-icon-action is-danger" aria-label={tx.t("sketch.basemap.remove", { file: u.file_name })} disabled={props.readOnly} onClick={() => props.onDelete(u.id)}><Trash2 size={13} /></button>
                </span>
              </div>
            ))}
            <button type="button" className="sk-action is-ghost sk-upload-btn" disabled={props.readOnly || props.busy} onClick={() => file.current?.click()}>
              <ImageUp size={14} /> {tx.t(props.busy ? "sketch.basemap.uploading" : "sketch.basemap.upload")}
            </button>
            <input
              ref={file}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/tiff,.jpg,.jpeg,.png,.tif,.tiff"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) props.onUpload(f);
              }}
            />
            <small className="sk-caption">{tx.t("sketch.basemap.uploadNote", { mb: 40 })}</small>
          </div>
          {props.data?.chosen_by === "design" && (
            <button type="button" className="sk-link-btn" disabled={props.readOnly} onClick={() => props.onChoose(null)}>{tx.t("sketch.basemap.companyDefault")}</button>
          )}
          {props.error && <p className="sk-error">{props.error}</p>}
        </div>
      )}
    </div>
  );
}

function ChoiceRow({ c, active, disabled, onPick }: { c: Choice; active: boolean; disabled: boolean; onPick: () => void }) {
  const tx = useSketchText();
  return (
    <li>
      <button type="button" role="radio" aria-checked={active} className={`sk-basemap-choice${active ? " is-active" : ""}`} disabled={disabled && !active} onClick={onPick}>
        <i className="sk-radio" aria-hidden>{active && <Check size={11} />}</i>
        <span className="sk-basemap-text">
          <b>{c.label}</b>
          <small>{!c.available && c.reason ? c.reason : tx.t(c.kind === "browser" ? "sketch.basemap.live" : c.licence ? "sketch.basemap.openData" : "sketch.basemap.satellite")}</small>
        </span>
        {c.licence && c.available && <span className="sk-licence">{c.licence}</span>}
      </button>
    </li>
  );
}

export type AlignMode = "drag" | "points";

/** The placing panel: drag or control points, the picture's opacity, the points so far and the fit. */
export function AlignPanel(props: {
  upload: Upload;
  mode: AlignMode;
  onMode: (m: AlignMode) => void;
  opacity: number;
  onOpacity: (v: number) => void;
  points: { image: [number, number]; enu?: [number, number] }[];
  miss: number | null;
  scaleCm: number | null;
  canSave: boolean;
  busy: boolean;
  error: string | null;
  onClearPoints: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const tx = useSketchText();
  const waiting = props.mode === "points" ? (props.points.length && !props.points[props.points.length - 1]!.enu ? "map" : "picture") : null;
  return (
    <div className="sk-align sk-glass" role="dialog" aria-label={tx.t("sketch.align.title")}>
      <div className="sk-align-head">
        <span className="sk-side-icon"><ImageUp size={16} /></span>
        <b>{tx.t("sketch.align.title")}</b>
        <small title={props.upload.file_name} dir="auto">{props.upload.file_name}</small>
      </div>
      <div className="sk-segmented is-align" role="radiogroup" aria-label={tx.t("sketch.align.how")} style={{ "--i": props.mode === "drag" ? 0 : 1, "--n": 2 } as CSSProperties}>
        <button type="button" role="radio" aria-checked={props.mode === "drag"} className={props.mode === "drag" ? "is-active" : ""} onClick={() => props.onMode("drag")}><Move size={14} /> {tx.t("sketch.align.drag")}</button>
        <button type="button" role="radio" aria-checked={props.mode === "points"} className={props.mode === "points" ? "is-active" : ""} onClick={() => props.onMode("points")}><MapPin size={14} /> {tx.t("sketch.basemap.source.controlPoints")}</button>
      </div>
      {props.mode === "drag" ? (
        <p className="sk-caption">{tx.t("sketch.align.dragCaption")}</p>
      ) : (
        <>
          <ol className="sk-align-steps">
            {[0, 1, 2].map((i) => {
              const p = props.points[i];
              return (
                <li key={i} className={p?.enu ? "is-done" : p ? "is-half" : i === props.points.length ? "is-current" : ""}>
                  <i>{i + 1}</i>
                  <span>{tx.t(p?.enu ? "sketch.align.placed" : p ? "sketch.align.sameSpot" : i < 2 ? "sketch.align.spotOnPicture" : "sketch.align.optionalThird")}</span>
                </li>
              );
            })}
          </ol>
          <p className="sk-caption">{tx.t(waiting === "map" ? "sketch.align.clickMap" : "sketch.align.clickPicture")}</p>
          {props.points.length > 0 && <button type="button" className="sk-link-btn" onClick={props.onClearPoints}>{tx.t("sketch.align.restart")}</button>}
        </>
      )}
      <label className="sk-align-opacity">
        <span>{tx.t("sketch.align.picture")}</span>
        <input type="range" min={0.15} max={1} step={0.05} value={props.opacity} onChange={(e) => props.onOpacity(Number(e.target.value))} aria-label={tx.t("sketch.align.opacity")} />
        <span>{tx.pct(Math.round(props.opacity * 100))}</span>
      </label>
      <div className="sk-align-fit">
        {props.scaleCm != null && <span>{tx.t("sketch.align.scale", { cm: tx.num(props.scaleCm, 1) })}</span>}
        {props.miss != null && <span className={props.miss > 0.3 ? "is-warn" : ""}>{props.points.length >= 3 ? tx.t("sketch.align.within", { cm: tx.num(props.miss * 100, 0) }) : tx.t("sketch.align.exact")}</span>}
      </div>
      {props.error && <p className="sk-error">{props.error}</p>}
      <div className="sk-actions">
        <button type="button" className="sk-action is-ghost" onClick={props.onCancel}>{tx.t("sketch.common.cancel")}</button>
        <button type="button" className="sk-action is-primary" disabled={!props.canSave || props.busy} onClick={props.onSave}><Check size={14} /> {tx.t(props.busy ? "sketch.common.saving" : "sketch.align.save")}</button>
      </div>
    </div>
  );
}
